import React, { FC } from "react";
import {
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  HStack,
  Box,
  Text,
  Select,
  Icon,
  IconButton,
  Skeleton,
} from "@chakra-ui/react";
import {
  IconInboxOff,
  IconChevronsLeft,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsRight,
} from "@tabler/icons-react";
import { formatCurrency, formatDate, formatDateTime } from "@Utils/index";

export type ColumnType =
  | "string"
  | "number"
  | "currency"
  | "date"
  | "datetime"
  | "boolean";
type Align = "left" | "center" | "right";
type Data = Record<string, any>;

export interface Column {
  label: string;
  key: string;
  type?: ColumnType;
  width?: string;
  headerAlign?: Align;
  render?: (value: any, item: Data) => React.ReactNode;
}

export interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export interface TableProps {
  columns: Column[];
  data?: Data[] | null;
  isLoading?: boolean;
  isFetching?: boolean;
  emptyStateTitle?: string;
  emptyStateMessage?: string;
  withBorder?: boolean;
  pagination?: TablePaginationProps;
  noScroll?: boolean;
  skeletonRows?: number;
  onRowClick?: (item: Data) => void;
}

// Acessa propriedades aninhadas por chave (ex: "tariff.tariffType")
const getNestedValue = (obj: Data, key: string): any => {
  return key
    .split(".")
    .reduce((acc, k) => (acc != null ? acc[k] : undefined), obj);
};

const formatValue = (value: any, column: Column): string => {
  if (value === undefined || value === null) return "—";
  switch (column.type) {
    case "currency":
      return formatCurrency(Number(value));
    case "date":
      return formatDate(String(value));
    case "datetime":
      return formatDateTime(String(value));
    case "boolean":
      return value ? "Sim" : "Não";
    case "number":
      return Number(value).toLocaleString("pt-BR");
    default:
      return String(value);
  }
};

const getAlign = (column: Column): Align => {
  if (column.headerAlign) return column.headerAlign;
  if (column.type === "currency" || column.type === "number") return "right";
  return "left";
};

const TableSkeleton: FC<{ rows: number; columns: number }> = ({
  rows,
  columns,
}) => (
  <Box p="12px">
    {Array.from({ length: rows }).map((_, r) => (
      <Flex key={r} gap="12px" py="8px">
        {Array.from({ length: columns }).map((_, c) => (
          <Skeleton key={c} height="16px" flex={1} borderRadius="4px" />
        ))}
      </Flex>
    ))}
  </Box>
);

const EmptyState: FC<{ title?: string; message?: string }> = ({
  title = "Nenhum registro encontrado",
  message = "Sem dados para exibir.",
}) => (
  <Flex direction="column" align="center" justify="center" py={10} gap={3}>
    <Box
      w="48px"
      h="48px"
      borderRadius="12px"
      bg="neutrals.background.weak-50"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Icon as={IconInboxOff} boxSize={5} color="neutrals.icon.soft-400" />
    </Box>
    <Box textAlign="center">
      <Text
        fontSize="sm"
        fontWeight="600"
        color="neutrals.text.strong-950"
        fontFamily="heading"
      >
        {title}
      </Text>
      <Text fontSize="xs" color="neutrals.text.soft-400" fontFamily="body" mt={1}>
        {message}
      </Text>
    </Box>
  </Flex>
);

const AppTable: FC<TableProps> = ({
  columns,
  data,
  isLoading = false,
  isFetching = false,
  emptyStateTitle,
  emptyStateMessage,
  withBorder = true,
  pagination,
  noScroll = false,
  skeletonRows = 10,
  onRowClick,
}) => {
  const hasData = data && data.length > 0;
  const showSkeleton = isLoading || isFetching;

  return (
    <Flex
      direction="column"
      bg="neutrals.surface.surface-0"
      border={withBorder ? "1px solid" : undefined}
      borderColor={withBorder ? "neutrals.stroke.soft-200" : undefined}
      borderRadius={withBorder ? "8px" : 0}
      overflow={noScroll ? undefined : "hidden"}
    >
      {showSkeleton ? (
        <TableSkeleton rows={skeletonRows} columns={columns.length} />
      ) : (
        <TableContainer
          overflowX="auto"
          overflowY={noScroll ? "visible" : "auto"}
          maxH={noScroll ? undefined : "400px"}
        >
          <ChakraTable
            variant="simple"
            size="sm"
            w="100%"
            minW={{ base: "720px", lg: 0 }}
            style={{ tableLayout: "fixed", width: "100%" }}
          >
            <Thead
              bg="neutrals.background.weak-50"
              position="sticky"
              top={0}
              zIndex={1}
            >
              <Tr>
                {columns.map((col) => {
                  const align = getAlign(col);
                  return (
                    <Th
                      key={col.key}
                      w={col.width}
                      px="8px"
                      py="10px"
                      textAlign={align}
                      borderBottom="1px solid"
                      borderColor="neutrals.stroke.soft-200"
                      textTransform="none"
                      letterSpacing="0"
                    >
                      <Text
                        fontSize="12px"
                        fontWeight="700"
                        color="neutrals.text.sub-600"
                        fontFamily="body"
                      >
                        {col.label}
                      </Text>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody bg="neutrals.surface.surface-0">
              {hasData ? (
                data!.map((item, rowIdx) => (
                  <Tr
                    key={rowIdx}
                    _hover={{ bg: "neutrals.background.weak-50" }}
                    transition="background 0.1s"
                    cursor={onRowClick ? "pointer" : undefined}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                  >
                    {columns.map((col) => {
                      const rawValue = getNestedValue(item, col.key);
                      const align = getAlign(col);
                      return (
                        <Td
                          key={col.key}
                          px="8px"
                          py="12px"
                          textAlign={align}
                          borderColor="neutrals.stroke.soft-200"
                          overflow="hidden"
                        >
                          {col.render ? (
                            col.render(rawValue, item)
                          ) : (
                            <Text
                              fontSize="13px"
                              fontWeight="400"
                              color="neutrals.text.strong-950"
                              fontFamily="body"
                            >
                              {formatValue(rawValue, col)}
                            </Text>
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={columns.length} border="none" p={0}>
                    <EmptyState
                      title={emptyStateTitle}
                      message={emptyStateMessage}
                    />
                  </Td>
                </Tr>
              )}
            </Tbody>
          </ChakraTable>
        </TableContainer>
      )}

      {pagination && (
        <Flex
          justify="space-between"
          align="center"
          px="12px"
          py="10px"
          borderTop="1px solid"
          borderColor="neutrals.stroke.soft-200"
        >
          <HStack spacing={3}>
            <Text fontSize="xs" color="neutrals.text.sub-600">
              Página {pagination.currentPage} de {pagination.totalPages} ·{" "}
              {pagination.totalItems} registros
            </Text>
            {pagination.onPageSizeChange && (
              <HStack spacing={1} align="center">
                <Text fontSize="xs" color="neutrals.text.sub-600">
                  Por página:
                </Text>
                <Select
                  size="xs"
                  w="70px"
                  value={pagination.pageSize}
                  onChange={(e) =>
                    pagination.onPageSizeChange!(Number(e.target.value))
                  }
                >
                  {(pagination.pageSizeOptions ?? [15, 25, 50, 100]).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </HStack>
            )}
          </HStack>
          <HStack spacing={1}>
            <IconButton
              aria-label="Primeira página"
              icon={<Icon as={IconChevronsLeft} boxSize={4} />}
              size="sm"
              variant="solid"
              isDisabled={pagination.currentPage <= 1}
              onClick={() => pagination.onPageChange(1)}
            />
            <IconButton
              aria-label="Página anterior"
              icon={<Icon as={IconChevronLeft} boxSize={4} />}
              size="sm"
              variant="solid"
              isDisabled={pagination.currentPage <= 1}
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
            />
            <IconButton
              aria-label="Próxima página"
              icon={<Icon as={IconChevronRight} boxSize={4} />}
              size="sm"
              variant="solid"
              isDisabled={pagination.currentPage >= pagination.totalPages}
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
            />
            <IconButton
              aria-label="Última página"
              icon={<Icon as={IconChevronsRight} boxSize={4} />}
              size="sm"
              variant="solid"
              isDisabled={pagination.currentPage >= pagination.totalPages}
              onClick={() => pagination.onPageChange(pagination.totalPages)}
            />
          </HStack>
        </Flex>
      )}
    </Flex>
  );
};

export default AppTable;
export type { Data };
